import { ref, computed, nextTick, type Ref, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import {
	ApiEvaluateSingleDriverResponse,
	ApiAnnotationDriverDay,
	ApiDutyViolationOrMet,
	ApiDuty,
	ApiFitnessData,
	ApiRosterScore,
} from '@/rennovation/planning-view/types/api/roster-response';
interface DebugDataDependencies {
	selectedDriver: ComputedRef<{ displayName: string; id: string } | null>;
	manualWorktimeBalances: ComputedRef<Array<{ day: { id: string }; amount: number }>>;
}

export function useDriverDebugData(driverId: string, range: { start: Ref<Date | null>; end: Ref<Date | null> }, deps: DebugDataDependencies) {
	const { t } = useI18n();
	const planningStore = usePlanningStore();
	const planningBridgeStore = usePlanningBridgeStore();

	const debugData = ref<ApiEvaluateSingleDriverResponse | null>(null);
	const isLoading = ref(false);

	const scoreKeys = ref<string[]>([]);
	const fitnessDataKeys = ref<string[]>([]);

	const setup = computed(() => planningBridgeStore.setup);

	// ─────────────────────────────
	// Derived
	// ─────────────────────────────
	const driverDays = computed(() => debugData.value?.annotations?.drivers?.[0]?.days ?? []);
	const driverAnnotation = computed(() => debugData.value?.annotations?.drivers?.[0] ?? null);

	// ─────────────────────────────
	// Build empty
	// ─────────────────────────────
	function buildEmptyDebugData() {
		return {
			totalScore: 0,
			annotations: {
				statistics: {},
				plugins: {
					Rotation: {
						rotationsByDriverIndex: {},
						rotationWeekdayByDayByDriver: [],
						rotationWeekdayByDayByDriverId: [],
					},
				},
				drivers: [
					{
						days: [],
						isVirtual: false,
						pensum: null,
						pensumHistory: [],
						workTime: 0,
						workTimeDifference: 0,
						restDaysDifference: 0,
					},
				],
				qualifications: {},
				previousStatistics: {},
				debug: false,
				days: [],
				rotationsByDriverIndex: {},
			},
			days: [],
			fitnessData: [],
			basicFitnessData: {},
			score: {} as ApiRosterScore,
			objectives: {},
			violations: 0,
		};
	}

	// ─────────────────────────────
	// Keys
	// ─────────────────────────────
	function prepareFitnessKeys(): void {
		const keySet = new Set<string>();

		for (const snapshot of debugData.value?.fitnessData ?? []) {
			for (const k of Object.keys(snapshot ?? {})) {
				keySet.add(k);
			}
		}

		fitnessDataKeys.value = Array.from(keySet);
	}

	function prepareScoreKeys(): void {
		const keyMap: Record<string, true> = {};

		for (const driver of debugData.value?.annotations?.drivers ?? []) {
			for (const day of driver.days ?? []) {
				for (const k of Object.keys(day.score ?? {})) {
					keyMap[k] = true;
				}
			}
		}

		scoreKeys.value = Object.keys(keyMap);
	}

	// ─────────────────────────────
	// Load
	// ─────────────────────────────
	// Improve the catch block in loadDebugData
	async function loadDebugData(): Promise<void> {
		if (!range.start.value || !range.end.value) return;

		isLoading.value = true;
		await nextTick();

		try {
			const from = range.start.value.toISOString().split('T')[0];
			const to = range.end.value.toISOString().split('T')[0];

			const result = await planningStore.evaluateSingleDriver(driverId, from, to, true);

			if (result && result.annotations && result.fitnessData) {
				debugData.value = result;
				prepareFitnessKeys();
				prepareScoreKeys();
			} else {
				debugData.value = buildEmptyDebugData();
			}
		} catch (error) {
			debugData.value = buildEmptyDebugData();
		} finally {
			isLoading.value = false;
		}
	}

	// ─────────────────────────────
	// UI helpers (moved from component)
	// ─────────────────────────────

	const driverDisplayName = computed(() => {
		if (!deps.selectedDriver?.value) return driverId;
		const d = deps.selectedDriver.value;
		return `${d.displayName} (${d.id})`;
	});

	const violationsDays = computed(() =>
		driverDays.value.map((day: ApiAnnotationDriverDay) => {
			const v = day?.duty?.violations;
			if (!v?.length) return { total: 0, count: 0, title: '' };

			return {
				total: v.reduce((s: number, a: ApiDutyViolationOrMet) => s + a.weight, 0),
				count: v.length,
				title: buildEvaluationTooltip(v),
			};
		})
	);

	const metDays = computed(() =>
		driverDays.value.map((day: ApiAnnotationDriverDay) => {
			const m = day?.duty?.met;
			if (!m?.length) return { total: 0, count: 0, sum: 0, title: '' };

			return {
				total: m.reduce((s: number, a: ApiDutyViolationOrMet) => s + a.weight, 0),
				count: m.length,
				sum: day.statistics?.metSum ?? 0,
				title: buildEvaluationTooltip(m),
			};
		})
	);

	const unmetDays = computed(() =>
		driverDays.value.map((day: ApiAnnotationDriverDay) => {
			const u = day?.duty?.unmet;
			if (!u?.length) return { total: 0, count: 0, sum: 0, title: '' };

			return {
				total: u.reduce((s: number, a: ApiDutyViolationOrMet) => s + a.weight, 0),
				count: u.length,
				sum: day.statistics?.unmetSum ?? 0,
				title: buildEvaluationTooltip(u),
			};
		})
	);

	// ─────────────────────────────
	// Style helpers
	// ─────────────────────────────
	function getShiftBackgroundColor(shift: string | number) {
		const map: Record<string | number, string> = {
			1: 'var(--ddd-shift-1)',
			2: 'var(--ddd-shift-2)',
			3: 'var(--ddd-shift-3)',
			4: 'var(--ddd-shift-4)',
		};
		return map[shift] ?? 'var(--ddd-bg-card)';
	}

	function getShiftCellStyle(day: ApiAnnotationDriverDay) {
		if (day?.shift == null) return { backgroundColor: 'var(--ddd-bg-card)' };
		return { backgroundColor: getShiftBackgroundColor(day.shift) };
	}

	function getStandbyCellStyle(day: ApiAnnotationDriverDay) {
		return day?.isStandby ? { backgroundColor: 'var(--ddd-standby-bg)' } : { backgroundColor: 'var(--ddd-bg-card)' };
	}

	interface DayEvaluation {
		total: number;
		count: number;
		title: string;
		sum?: number;
	}
	function getMetCellStyle(m: DayEvaluation) {
		if (!m?.total) return { backgroundColor: 'var(--ddd-bg-card)' };
		return { backgroundColor: 'var(--ddd-met-bg)' };
	}

	function getUnmetCellStyle(u: DayEvaluation) {
		if (!u?.total) return { backgroundColor: 'var(--ddd-bg-card)' };
		return { backgroundColor: 'var(--ddd-unmet-bg)' };
	}

	function getViolCellStyle(v: DayEvaluation) {
		if (!v?.total) return { backgroundColor: 'var(--ddd-bg-card)' };
		return { backgroundColor: 'var(--ddd-viol-bg)' };
	}

	// ─────────────────────────────
	// Cell helpers
	// ─────────────────────────────
	function getManualAmount(dayId: string | undefined): number {
		if (!dayId) return 0;
		const bal = deps.manualWorktimeBalances?.value?.find((b) => b.day.id === dayId);
		return bal ? bal.amount : 0;
	}

	function dutyTooltip(duty: ApiDuty | null): string {
		if (!duty?.type) return '';
		if (duty.isRestSunday) return <string>t('duty_dayType_restSunday');
		return <string>t(`duty_dayType_${duty.type}`);
	}

	// ─────────────────────────────
	// Format utilities
	// ─────────────────────────────

	function weekdayShort(dateStr: string): string {
		return new Date(dateStr).toLocaleDateString(undefined, { weekday: 'short' });
	}

	function formatDayDate(dateStr: string): string {
		return new Date(dateStr).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
	}

	function buildEvaluationTooltip(items: ApiDutyViolationOrMet[]): string {
		return items.map((i) => t(i.messageKey)).join('\n');
	}

	function renderFitnessCell(data: ApiFitnessData | null, key: keyof ApiFitnessData): string {
		const val = data?.[key];
		if (val == null) return '';
		if (typeof val === 'object') return JSON.stringify(val);
		return String(val);
	}

	return {
		// state
		debugData,
		isLoading,
		scoreKeys,
		fitnessDataKeys,
		setup,

		// derived
		driverDays,
		driverAnnotation,
		driverDisplayName,
		violationsDays,
		metDays,
		unmetDays,

		// actions
		loadDebugData,

		// style helpers
		getShiftBackgroundColor,
		getShiftCellStyle,
		getStandbyCellStyle,
		getMetCellStyle,
		getUnmetCellStyle,
		getViolCellStyle,

		// cell helpers
		dutyTooltip,
		getManualAmount,
		renderFitnessCell,

		// format utils
		weekdayShort,
		formatDayDate,
	};
}
